// Copied either from:
//   - entry.client.hydrateRoot.jsx
//   - entry.client.reactDOMHydrate.jsx

// There's 2 parts to being able to use fancy React 18 Suspense support (with remix's Await) feature:
//
//   1. Updating entry.client.jsx to use hydrateRoot instead of ReactDOM.hydrate (React 18)
//   2. Updating entry.server.jsx to use renderToPipeableStream (Suspense)
//
// #1 can be done alone, but #2 requires #1 to be done first.

// The main problem with enabling #1 today is that there's TONs of hydration issues
// reported in Sentry, which lead to a blank page, breaking the UI for users.

// When React 19 is released, we'll get proper hydration error messages,
// Allowing us to debug the source of the hydration issues and fix them.

import * as Sentry from "@sentry/remix";

import { StrictMode, useState, useCallback } from "react";
import { hydrate as DEPRECATED_REACT_17_HYDRATE } from "react-dom";
import createEmotionCache from "@emotion/cache";
import { CacheProvider } from "@emotion/react";
import { RemixBrowser } from "@remix-run/react";

import ClientStyleContext from "./clientStyleContext.js";
import { initPostHog } from "#app/utils/posthog.client.js";
import { initSentry } from "#app/utils/sentry.client.js";

// Start loading PostHog now, beside hydration rather than ahead of it (see
// posthog.client.js): anonymous pageviews/searches are still captured before
// the user is identified. initPostHog() is a no-op without a project token or
// in the test env.
initPostHog();

// Start Sentry (config in sentry.client.js). No-op in the test env / before
// window.env is set.
initSentry();

function ClientCacheProvider({ children }) {
  const [cache, setCache] = useState(createEmotionCache({ key: "css" }));

  const reset = useCallback(() => {
    setCache(createEmotionCache({ key: "css" }));
  }, []);

  return (
    <ClientStyleContext.Provider value={{ reset }}>
      <CacheProvider value={cache}>{children}</CacheProvider>
    </ClientStyleContext.Provider>
  );
}

const hydrate = () => {
  DEPRECATED_REACT_17_HYDRATE(
    <StrictMode>
      <ClientCacheProvider>
        <RemixBrowser />
      </ClientCacheProvider>
    </StrictMode>,
    document,
  );
};

if (window.requestIdleCallback) {
  window.requestIdleCallback(hydrate);
} else {
  // Safari doesn't support requestIdleCallback
  // https://caniuse.com/requestidlecallback
  setTimeout(hydrate, 1);
}

if ("serviceWorker" in navigator) {
  const swVersion =
    window.env.GIT_COMMIT_SHA || window.env.REMIX_VERSION || "production";
  const swUrl = `/entry.worker.js?v=${swVersion}`;

  navigator.serviceWorker
    .register(swUrl, { updateViaCache: "none" })
    .then((registration) => {
      registration.update().catch((err) => {
        // WebKit reports transient network drops (page backgrounded mid-fetch,
        // connection lost) as TypeError "Load failed". Benign: register() has
        // already succeeded, so the user still has a current worker.
        if (err instanceof TypeError) return;
        try {
          Sentry.captureException(err, {
            level: "warning",
            tags: { area: "service_worker" },
            extra: { reason: "registration.update() failed" },
          });
        } catch (_) {}
      });
      return navigator.serviceWorker.ready;
    })
    .then(() => {
      if (navigator.serviceWorker.controller) {
        navigator.serviceWorker.controller.postMessage({
          type: "SYNC_REMIX_MANIFEST",
          manifest: window.__remixManifest,
        });
      } else {
        navigator.serviceWorker.addEventListener("controllerchange", () => {
          navigator.serviceWorker.controller?.postMessage({
            type: "SYNC_REMIX_MANIFEST",
            manifest: window.__remixManifest,
          });
        });
      }
    })
    .catch((error) => {
      console.error("Service worker registration failed", error);
    });
}
